import type { RouteObject } from "react-router-dom";
import { Navigate, useLocation } from "react-router-dom";
import CrmLayout from "./CrmLayout";
import { FUNCTIONALITIES, CRM_BASE, crmDefaultRoute, menuItemPath } from "./config";
import { menuRoutePaths, usableRedirects } from "./navPaths";
import QuoteBuilder from "./sections/QuoteBuilder";
import { QUOTES_PATH } from "./services/quotePaths";
import { isPlatformFeatureEnabled } from "../../config/platformFeatures";

/** Redirige a la ruta nueva sin perder los parámetros ni el ancla del enlace original. */
function LegacyRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${CRM_BASE}/${to}${search}${hash}`} replace />;
}

// Builds the nested route tree for /crm from the nav config so the sub-menu and
// the routes never drift apart. One child route per sub-item, plus index
// redirects (section -> first functionality, functionality -> its dashboard).
// Mirrors Layouts/nuvex-access/routes.tsx.
const children: RouteObject[] = [
  { index: true, element: <Navigate to={crmDefaultRoute} replace /> },
];

for (const f of FUNCTIONALITIES) {
  children.push({
    path: f.slug,
    element: <Navigate to={`${CRM_BASE}/${f.slug}/${menuItemPath(f.menu[0])}`} replace />,
  });
  for (const item of f.menu) {
    if (item.children?.length) {
      // Parent: redirect to its first child, then register one route per child.
      children.push({
        path: `${f.slug}/${item.id}`,
        element: <Navigate to={`${CRM_BASE}/${f.slug}/${item.id}/${item.children[0].id}`} replace />,
      });
      for (const child of item.children) {
        children.push({ path: `${f.slug}/${item.id}/${child.id}`, element: child.element });
      }
    } else {
      children.push({ path: `${f.slug}/${item.id}`, element: item.element });
    }
  }
}

// Armador de una cotización (#1545): `/crm/negocios/negocios/cotizaciones/:quoteId`
// («nueva» para una en blanco). Vive bajo la ruta de la lista, así que lo gobierna
// el mismo derecho que la lista.
// Donde la plataforma apaga las cotizaciones del CRM (WAYAK) no se registra.
if (isPlatformFeatureEnabled("crm-facturacion")) {
  children.push({ path: `${QUOTES_PATH.slice(CRM_BASE.length + 1)}/:quoteId`, element: <QuoteBuilder /> });
}

// Rutas que cambiaron de lugar: las anteriores redirigen a las nuevas.
const livePaths = FUNCTIONALITIES.flatMap((f) => menuRoutePaths(f.slug, f.menu));
for (const [from, to] of usableRedirects(livePaths)) {
  children.push({ path: from, element: <LegacyRedirect to={to} /> });
}

export const crmRoute: RouteObject = {
  path: CRM_BASE,
  element: <CrmLayout />,
  children,
};
